import { useState, useEffect } from 'react';
import cogoToast from 'cogo-toast';
import { addProduct, updateProduct, getProduct } from '../../../../api/products';
import { transformProductImages } from '../lib/imageHelpers';

/**
 * Parse additional_descriptions from string to object if needed
 * @param {string|object} additionalDescriptions - The additional_descriptions field from the product
 * @returns {object} Parsed object or empty object if parsing fails
 */
const parseAdditionalDescriptions = additionalDescriptions => {
  if (!additionalDescriptions) {
    return {};
  }

  if (typeof additionalDescriptions === 'string') {
    try {
      return JSON.parse(additionalDescriptions);
    } catch (e) {
      console.error('Failed to parse additional_descriptions:', e);
      return {};
    }
  }

  return additionalDescriptions;
};

/**
 * @param {number} productId - Product ID to fetch
 * @param {Function} resetImageChanges - Function to reset image tracking
 * @param {Function} setImages - Function to update images state
 * @param {Function} setTitle - Function to update title state
 * @param {Function} setSelectedBrand - Function to update brand state
 * @param {Function} setSelectedCategory - Function to update category state
 * @param {Function} setIsHidden - Function to update isHidden state
 * @param {Function} setIsSeoPrerendered - Function to update isSeoPrerendered state
 * @param {Object} categories - Categories lookup object
 * @param {Function} setDescription - Function to update description state
 * @param {Function} setDescriptionWhyBuy - Function to update descriptionWhyBuy state
 * @param {Function} setDescriptionWhatPeopleSay - Function to update descriptionWhatPeopleSay state
 * @param {Function} setIsDescriptionManual - Function to update isDescriptionManual state
 * @param {Function} setIsDescriptionWhyBuyManual - Function to update isDescriptionWhyBuyManual state
 * @param {Function} setIsDescriptionWhatPeopleSayManual - Function to update isDescriptionWhatPeopleSayManual state
 * @returns {Object} Product operations state and functions
 */
export const useProduct = (
  productId,
  resetImageChanges,
  setImages,
  setTitle,
  setSelectedBrand,
  setSelectedCategory,
  setIsHidden,
  setIsSeoPrerendered,
  categories,
  setDescription,
  setDescriptionWhyBuy,
  setDescriptionWhatPeopleSay,
  setIsDescriptionManual,
  setIsDescriptionWhyBuyManual,
  setIsDescriptionWhatPeopleSayManual
) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [duplicateProduct, setDuplicateProduct] = useState(null);

  useEffect(() => {
    if (productId) {
      const controller = new AbortController();
      setIsLoading(true);
      setLoadError(null);

      getProduct(productId, { fullData: 1 }, { signal: controller.signal })
        .then(fetchedProduct => {
          if (fetchedProduct.images) {
            setImages(transformProductImages(fetchedProduct.images));
          }

          setTitle(fetchedProduct.title || '');

          if (fetchedProduct.all_brand) {
            setSelectedBrand({
              value: fetchedProduct.all_brand.id,
              label: fetchedProduct.all_brand.name
            });
          }

          if (fetchedProduct.Category_id && categories[fetchedProduct.Category_id]) {
            setSelectedCategory({
              value: fetchedProduct.Category_id,
              label: categories[fetchedProduct.Category_id].name
            });
          }

          setIsHidden(fetchedProduct.isHidden || false);
          setIsSeoPrerendered(fetchedProduct.isSeoPrerendered || false);

          // Load description fields
          setDescription(fetchedProduct.description || '');

          const additionalDescriptions = parseAdditionalDescriptions(fetchedProduct.additional_descriptions);
          setDescriptionWhyBuy(additionalDescriptions.description_why_buy || '');
          setDescriptionWhatPeopleSay(additionalDescriptions.description_what_people_say || '');
          setIsDescriptionManual(additionalDescriptions.is_description_manual || false);
          setIsDescriptionWhyBuyManual(additionalDescriptions.is_description_why_buy_manual || false);
          setIsDescriptionWhatPeopleSayManual(additionalDescriptions.is_description_what_people_say_manual || false);
        })
        .catch(error => {
          if (error?.name !== 'AbortError') {
            console.error('Failed to fetch product:', error);
            setLoadError(error.message || 'Failed to load product');
            cogoToast.error('Failed to load product');
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsLoading(false);
          }
        });

      return () => controller.abort();
    }
  }, [productId, categories]);

  const refreshProductData = async productId => {
    try {
      const freshProduct = await getProduct(productId, { fullData: 1 });

      resetImageChanges();

      if (freshProduct.images) {
        setImages(transformProductImages(freshProduct.images));
      }

      setTitle(freshProduct.title || '');

      if (freshProduct.all_brand) {
        setSelectedBrand({
          value: freshProduct.all_brand.id,
          label: freshProduct.all_brand.name
        });
      }

      if (freshProduct.Category_id && categories[freshProduct.Category_id]) {
        setSelectedCategory({
          value: freshProduct.Category_id,
          label: categories[freshProduct.Category_id].name
        });
      }

      setIsHidden(freshProduct.isHidden || false);
      setIsSeoPrerendered(freshProduct.isSeoPrerendered || false);

      // Refresh description fields
      setDescription(freshProduct.description || '');

      const additionalDescriptions = parseAdditionalDescriptions(freshProduct.additional_descriptions);
      setDescriptionWhyBuy(additionalDescriptions.description_why_buy || '');
      setDescriptionWhatPeopleSay(additionalDescriptions.description_what_people_say || '');
      setIsDescriptionManual(additionalDescriptions.is_description_manual || false);
      setIsDescriptionWhyBuyManual(additionalDescriptions.is_description_why_buy_manual || false);
      setIsDescriptionWhatPeopleSayManual(additionalDescriptions.is_description_what_people_say_manual || false);

      return freshProduct;
    } catch (error) {
      console.error('Failed to refresh product data:', error);
      throw error;
    }
  };

  const save = async (formData, imageChanges, onSave, userCreatedBrand) => {
    const {
      title,
      selectedBrand,
      selectedCategory,
      images,
      isHidden,
      isSeoPrerendered,
      description,
      descriptionWhyBuy,
      descriptionWhatPeopleSay,
      isDescriptionManual,
      isDescriptionWhyBuyManual,
      isDescriptionWhatPeopleSayManual
    } = formData;

    // Build additional_descriptions object
    const additional_descriptions = {};

    // Only include fields that have values or manual flags set
    if (descriptionWhyBuy || isDescriptionWhyBuyManual) {
      additional_descriptions.description_why_buy = descriptionWhyBuy;
      additional_descriptions.is_description_why_buy_manual = isDescriptionWhyBuyManual;
    }

    if (descriptionWhatPeopleSay || isDescriptionWhatPeopleSayManual) {
      additional_descriptions.description_what_people_say = descriptionWhatPeopleSay;
      additional_descriptions.is_description_what_people_say_manual = isDescriptionWhatPeopleSayManual;
    }

    if (isDescriptionManual) {
      additional_descriptions.is_description_manual = isDescriptionManual;
    }

    // Set last_updated so the nightly generation script skips this product
    if (Object.keys(additional_descriptions).length > 0) {
      additional_descriptions.last_updated = new Date().toISOString();
    }

    const basePayload = {
      title: title,
      brand: selectedBrand?.label,
      AllBrand_id: selectedBrand?.value,
      Category_id: selectedCategory?.value,
      isHidden: isHidden,
      isSeoPrerendered: isSeoPrerendered,
      description: description || null,
      forceRebuildDescription: false
    };

    // Only include additional_descriptions if it has content
    if (Object.keys(additional_descriptions).length > 0) {
      basePayload.additional_descriptions = additional_descriptions;
    }

    try {
      setIsSaving(true);
      setDuplicateProduct(null);

      if (productId) {
        await updateProduct(productId, {
          ...basePayload,
          imageOperations: imageChanges
        });

        const refreshedProduct = await refreshProductData(productId);

        onSave?.({ product: refreshedProduct, userCreatedBrand, userCreatedProduct: false });
        cogoToast.success('Successfully updated product');
      } else {
        const newProduct = await addProduct({
          ...basePayload,
          images
        });

        const refreshedProduct = await refreshProductData(newProduct.id);

        onSave?.({ product: refreshedProduct, userCreatedBrand, userCreatedProduct: true });
        cogoToast.success('Successfully added product');
      }
    } catch (error) {
      if (error?.code === 'duplicate_product' && error.extra?.existingProduct) {
        setDuplicateProduct(error.extra.existingProduct);
        return;
      }
      const message = typeof error === 'string' ? error : error?.message;
      cogoToast.error(message || `There was an issue ${productId ? 'updating' : 'adding'} the product`, {
        hideAfter: 5
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isSaving,
    isLoading,
    loadError,
    save,
    refreshProductData,
    duplicateProduct,
    setDuplicateProduct
  };
};
